import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import IconButton from "@mui/material/IconButton";
import GridViewIcon from "@mui/icons-material/GridView";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { locateUser, toggleFullscreen } from "../../../utils/MapboxUtils";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import NavigationIcon from "@mui/icons-material/Navigation";
import "./MapToolbar.css";
import { PITCH_MAX, PITCH_STEP, STYLES } from "../../../conts/constants";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LayersIcon from "@mui/icons-material/Layers";
const thumb = (styleUrl) => {
  const path = styleUrl.replace("mapbox://styles/", "");
  return `https://api.mapbox.com/styles/v1/${path}/static/8.5,47,4/160x110?logo=false&attribution=false&access_token=${process.env.REACT_APP_MAPBOX_PK}`;
};

const MapToolbar = ({
  mapRef,
  selectedStyle,
  onStyleChange,
  legendOpen,
  onToggleLegend,
  legendAvailable = true,
}) => {
  const [showStyles, setShowStyles] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [locating, setLocating] = useState(false);
  const markerRef = useRef(null);
  const toolbarRef = useRef(null);
  const [bearing, setBearing] = useState(0);
  const dragRef = useRef({ active: false, moved: false, x: 0, y: 0 });
  const [pitch, setPitch] = useState(0);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onPitch = () => setPitch(Math.round(map.getPitch()));
    onPitch();
    map.on("pitch", onPitch);
    return () => map.off("pitch", onPitch);
  }, [mapRef]);

  const changePitch = (delta) => {
    const map = mapRef.current;
    if (!map) return;
    const next = Math.max(0, Math.min(PITCH_MAX, map.getPitch() + delta));
    map.easeTo({ pitch: next, duration: 300 });
  };
  const handleCompassDown = (e) => {
    const map = mapRef.current;
    if (!map) return;
    e.preventDefault();
    dragRef.current = {
      active: true,
      moved: false,
      x: e.clientX,
      y: e.clientY,
    };

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d.active) return;
      const dx = ev.clientX - d.x;
      const dy = ev.clientY - d.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
      // horizontal drag rotates, vertical drag tilts (same as the default control)
      map.setBearing(map.getBearing() + dx * 0.8);
      map.setPitch(Math.max(0, Math.min(60, map.getPitch() - dy * 0.5)));
      d.x = ev.clientX;
      d.y = ev.clientY;
    };

    const onUp = () => {
      const wasDrag = dragRef.current.moved;
      dragRef.current.active = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      // a simple click (no drag) resets north
      if (!wasDrag) map.resetNorthPitch();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onRotate = () => setBearing(map.getBearing());
    map.on("rotate", onRotate);
    return () => map.off("rotate", onRotate);
  }, [mapRef]);
  // close the styles popup when clicking outside
  useEffect(() => {
    const onClickOutside = (e) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target))
        setShowStyles(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // keep the icon in sync (also when the user presses Esc) and resize the map
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => mapRef.current?.resize(), 100);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [mapRef]);

  useEffect(() => () => markerRef.current?.remove(), []);

  const handleLocate = () => {
    setLocating(true);
    locateUser(mapRef.current, (center) => {
      if (center) {
        markerRef.current?.remove();
        markerRef.current = new mapboxgl.Marker({ color: "#5A55A0" })
          .setLngLat(center)
          .addTo(mapRef.current);
      }
      setLocating(false);
    });
  };

  return (
    <>
      <div className="map-toolbar" ref={toolbarRef}>
        {showStyles && (
          <div className="map-toolbar__styles">
            {STYLES.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`map-toolbar__style ${selectedStyle === s.url ? "map-toolbar__style--active" : ""}`}
                onClick={() => {
                  onStyleChange(s.url);
                  setShowStyles(false);
                }}
              >
                <img src={thumb(s.url)} alt="" />
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        )}

        <IconButton
          className={`map-toolbar__btn ${showStyles ? "map-toolbar__btn--active" : ""}`}
          onClick={() => setShowStyles((v) => !v)}
          aria-label="Styles de carte"
          title="Styles de carte"
        >
          <GridViewIcon fontSize="small" />
        </IconButton>
        <IconButton
          className={`map-toolbar__btn ${locating ? "map-toolbar__btn--active" : ""}`}
          onClick={handleLocate}
          aria-label="Ma position"
          title="Ma position"
        >
          <MyLocationIcon fontSize="small" />
        </IconButton>
        <IconButton
          className="map-toolbar__btn"
          onClick={toggleFullscreen}
          aria-label="Plein écran"
          title="Plein écran"
        >
          {isFullscreen ? (
            <FullscreenExitIcon fontSize="small" />
          ) : (
            <FullscreenIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          className={`map-toolbar__btn ${legendOpen && legendAvailable ? "map-toolbar__btn--active" : ""}`}
          onClick={onToggleLegend}
          disabled={!legendAvailable}
          aria-label={legendOpen ? "Masquer la légende" : "Afficher la légende"}
          aria-pressed={legendOpen}
          title={legendOpen ? "Masquer la légende" : "Afficher la légende"}
        >
          <LayersIcon fontSize="small" />
        </IconButton>
      </div>
      <div className="map-toolbar map-toolbar--bottom">
        <IconButton
          className="map-toolbar__btn"
          onClick={() => mapRef.current?.zoomIn()}
          aria-label="Zoom avant"
          title="Zoom avant"
        >
          <AddIcon fontSize="small" />
        </IconButton>
        <IconButton
          className="map-toolbar__btn"
          onClick={() => mapRef.current?.zoomOut()}
          aria-label="Zoom arrière"
          title="Zoom arrière"
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <IconButton
          className="map-toolbar__btn"
          onClick={() => changePitch(PITCH_STEP)}
          disabled={pitch >= PITCH_MAX}
          aria-label="Augmenter l’inclinaison"
          title="Augmenter l’inclinaison"
        >
          <KeyboardArrowUpIcon fontSize="small" />
        </IconButton>
        {/* <div
          className="map-toolbar__readout"
          aria-live="off"
          title="Inclinaison"
        >
          {pitch}°
        </div> */}
        <IconButton
          className="map-toolbar__btn"
          onClick={() => changePitch(-PITCH_STEP)}
          disabled={pitch <= 0}
          aria-label="Réduire l’inclinaison"
          title="Réduire l’inclinaison"
        >
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
        <IconButton
          className="map-toolbar__btn"
          onPointerDown={handleCompassDown}
          aria-label="Nord"
          title="Cliquer pour orienter au nord, glisser pour pivoter"
          style={{ touchAction: "none", cursor: "grab" }}
        >
          <NavigationIcon
            fontSize="small"
            style={{ transform: `rotate(${-bearing}deg)` }}
          />
        </IconButton>
      </div>
    </>
  );
};

export default MapToolbar;
